import styled from 'styled-components';

import { Theme, useTheme } from '@/services/theme';
import { breakpoints } from '@/shared/styles';
import { APP_DOMAIN } from '@/core/constants/app-domain';
import { Audits } from '@/shared/contants/Audits';

import { Resources } from './components/Resources';
import { resourcesList } from './fixtures';
import { Typography } from '../Typography';
import { PageContainer } from '../PageContainer';
import { Flex } from '../Flex';
import { Box } from '../Box';

const StyledFooter = styled.footer`
  background-color: ${({ theme }: { theme: Theme }) => theme.colors.background.footer};
  color: ${({ theme }: { theme: Theme }) => theme.colors.text.primary};
  font-size: 1.4rem;
  margin-top: auto;
  border-top-left-radius: 64px;
  border-top-right-radius: 64px;

  h3 {
    font-family: Gramatika;
  }

  p,
  a {
    font-family: Helvetica, sans-serif;
  }
`;

const StyledPageContainer = styled(PageContainer)`
  padding-top: 8rem;
  padding-bottom: 8rem;
`;

const StyledContent = styled.div`
  display: grid;
  grid-template: repeat(4, auto) / 100%;
  gap: 5.6rem 3.2rem;

  @media (min-width: ${breakpoints.mobileXL}px) {
    grid-template: auto auto / repeat(3, 1fr);
    row-gap: 6.4rem;
  }

  @media (min-width: ${breakpoints.tabletXL}px) {
    grid-template: 1fr / repeat(5, 1fr);
  }
`;

const StyledInfo = styled.div`
  grid-area: 4/1/5/2;

  @media (min-width: ${breakpoints.mobileXL}px) {
    grid-area: 2/1/3/4;
  }

  @media (min-width: ${breakpoints.tabletXL}px) {
    grid-area: 1/1/2/3;
  }
`;

const StyledAudit = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  height: 34px;
  width: 100px;
  border-radius: 4px;

  border: 1px solid ${({ theme }: { theme: Theme }) => theme.colors.newGray3};

  & svg {
    width: 100%;
    max-width: 70px;
  }
`;

type NewFooterProps = {
  containerClassName?: string;
  showAudits?: boolean;
  showV1Link?: boolean;
};

export const NewFooter = ({ containerClassName, showAudits, showV1Link }: NewFooterProps) => {
  const { theme } = useTheme();

  return (
    <StyledFooter theme={theme} className={containerClassName}>
      <StyledPageContainer>
        <StyledContent>
          <StyledInfo>
            <Typography variant="h3" mb={2.4}>
              Mellow Protocol
            </Typography>
            <Typography color="secondary" variant="body1" mb={1.6}>
              Permissionless ecosystem for capital efficiency, complex liquidity management
              strategies and tools.
            </Typography>
            <Typography color="secondary" variant="body1" mb={1.6}>
              © {new Date().getFullYear()} Mellow Protocol. All rights reserved.
            </Typography>

            {showAudits && (
              <>
                <Typography color="secondary" variant="body1" mt={4} mb={1.6}>
                  Audited by:
                </Typography>
                <Flex direction="row" gap="6px" wrap="wrap">
                  {Audits.map((audit, idx) => (
                    <StyledAudit
                      key={`audit_${idx}`}
                      href={audit.auditorLink}
                      theme={theme}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {audit.logo}
                    </StyledAudit>
                  ))}
                </Flex>
              </>
            )}

            {showV1Link && (
              <Typography
                as="a"
                color="secondary"
                variant="body1"
                target="_blank"
                href={`${APP_DOMAIN}/products`}
                rel="noreferrer"
              >
                Go to Mellow ALM
              </Typography>
            )}
          </StyledInfo>

          {resourcesList.map(item => (
            <Resources key={item.title} {...item} />
          ))}
        </StyledContent>

        <Box width="100%">
          <Typography fontFamily="gramatika" color="secondary" variant="body1" size={1.2}>
            The Mellow App is provided for informational purposes only. Nothing contained in the App
            constitutes investment, financial, or other advice, or endorsement of any product or
            service. Vault-related information is sourced from third parties and may be inaccurate,
            incomplete, or outdated.
          </Typography>
          <br />
          <Typography fontFamily="gramatika" color="secondary" variant="body1" size={1.2}>
            APYs presented in the App are calculated from a limited short-term sample, providing a
            simplified estimate based on the historical data rather than a guaranteed 12-month
            result. APY/APR is not a guarantee of future returns. Realized yields may vary.
          </Typography>
        </Box>
      </StyledPageContainer>
    </StyledFooter>
  );
};
